import { Dialog } from '@angular/cdk/dialog';
import { Component, EventEmitter, Output } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  UntypedFormControl,
  Validators
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ROUTES } from '@common/interfaces';
import { CommonHttpService } from 'libs/common/src/lib/service/common-http.service';
import { ToastrService } from 'ngx-toastr';
import { GenericConfirmationPopUpComponent } from '../generic-confirmation-pop-up/generic-confirmation-pop-up.component';

@Component({
  selector: 'app-add-accessory-modal',
  templateUrl: './add-accessory-modal.component.html',
  styleUrls: ['./add-accessory-modal.component.scss']
})
export class AddAccessoryModalComponent {
  @Output() closeModal = new EventEmitter();
  @Output() alertBoxEmitter = new EventEmitter();
  pageTitle: string = '';
  maxDate: string = '';
  selectedCategory: any;
  quantityError: string = '';
  backRouteLink: { path: string; params: any } | undefined;
  workspaceId = localStorage.getItem('workspaceId');
  loaderView: boolean = true;
  accessoryTypeId!: string;
  accessoryId: string | null = null;
  ROUTES = ROUTES;
  accessoryForm!: FormGroup;
  accessoryTypeOptions: any;
  vendorNames: Record<string, any>[] = [];
  accessoryCode!: string;
  acronyms: string[] = [];

  accessoryTypeData: { [key: string]: any } = {};
  clients: any[] = [];
  selectedValue: string = '';
  accessoryDetails: any;
  properties: Record<string, any> = {};
  isCategorySelected: boolean = false;

  constructor(
    private commonHttpService: CommonHttpService,
    public dialog: Dialog,
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private toastrService: ToastrService,
    private fb: FormBuilder
  ) { }

  ngOnInit(): void {
    this.loaderView = true;
    const today = new Date();
    this.maxDate = today.toISOString().split('T')[0];
    this.accessoryForm = this.fb.group({
      accessoryCategory: ['', Validators.required],
      name: ['', [Validators.required, Validators.pattern(/^[A-Za-z0-9]+(?: [A-Za-z0-9]+)*\s?$/)]],
      purchaseDate: ['', Validators.required],
      quantity: ['', [Validators.required, Validators.maxLength(5), Validators.pattern(/^[0-9]+$/)]],
    });

    this.accessoryId = this.activatedRoute.snapshot.paramMap.get('id');

    // Initialization logic here
    this.loadAllaccessoryList();

    this.pageTitle = 'Add Accessory';
    if (this.accessoryId) {
      this.pageTitle = 'Edit Accessory';
      this.loaderView = true;
      this.getaccessoryDetails();
    }

    !this.accessoryId && (this.loaderView = false);
  }

  loadAllaccessoryList() {
    this.commonHttpService.getAllAccessoryType().subscribe({
      next: (response: any) => {
        this.loaderView = false
        this.accessoryTypeOptions = response.data.map((accessory: any) => ({
          id: accessory.id,
          name: accessory.name
        }));
      }
    })
  }

  getValueOfCategory(event: any) {
    this.accessoryTypeId = event;

    this.selectedValue = this.accessoryTypeOptions?.find(
      (option: any) => option.id === event
    )?.name;

    this.accessoryForm.get('accessoryCategory')?.setValue(this.selectedValue);

    this.commonHttpService.getAccessoryTypeDetails(this.accessoryTypeId).subscribe({
      next: (res: any) => {
        this.accessoryTypeData = res.data;
        this.addConfigControls();
        this.accessoryTypeData['categories'] = this.accessoryTypeData[
          'categories'
        ].filter((category: any) =>
          this.accessoryTypeData['configs'].some(
            (config: any) => config.category.toLowerCase() === category.name.toLowerCase()
          )
        );
        if (this.accessoryId) {
          this.fetchCategoryValueAndUpdateaccessory();
        }
        this.isCategorySelected = true;
      },
      error: (err: any) => {
        this.toastrService.error('accessory type not found', '', {
          toastClass: 'toaster-wrapper',
        });
      },
    });
  }

  getConfigsByCategory(categoryName: string) {
    return this.accessoryTypeData['configs'].filter(
      (config: any) => config.category.toLowerCase() === categoryName.toLowerCase()
    );
  }
  preventMoreThanFiveDigits(event: KeyboardEvent) {
    const input = event.target as HTMLInputElement;
    const allowedKeys = ['Backspace', 'ArrowLeft', 'ArrowRight', 'Tab', 'Delete'];

    if (event.key === 'Backspace' || event.key === 'Delete') {
      setTimeout(() => {
        if (input.value.length <= 5 && /^[0-9]*$/.test(input.value)) {
          this.quantityError = '';
        }
      }, 0);
      return;
    }

    if (['-', 'e', '+', '.'].includes(event.key)) {
      event.preventDefault();
      this.quantityError = 'Only positive numbers are allowed'
      return;
    }
    if (allowedKeys.includes(event.key)) {
      return;
    }
    if (input.value.length >= 5) {
      event.preventDefault();
      this.quantityError = 'Quantity cannot be over 5 digits'
    }
  }

  addConfigControls(): void {
    if (!this.accessoryTypeData?.['configs']) return;

    for (const config of this.accessoryTypeData['configs']) {
      this.accessoryForm.addControl(
        config.name,
        new UntypedFormControl(
          '',
          config.isRequired ? Validators.required : null
        )
      );


      if (config.units?.length) {
        this.accessoryForm.addControl(
          config.name + 'Unit',
          new UntypedFormControl(
            '',
            config.isRequired ? Validators.required : null
          )
        );
      }
    }
  }

  onSelectionChange(event: any, controlName: string) {
    this.accessoryForm.get(controlName)?.setValue(event);
    this.properties[controlName] = event;
  }

  onAccessoryCodeChange($event: any) {
    this.accessoryCode = $event.target.value;
    this.accessoryForm.get('accessoryCode')?.setValue(this.accessoryCode);
  }

  onSubmit(): void {
    this.accessoryForm.updateValueAndValidity();
    this.accessoryForm.markAllAsTouched();
    if (this.accessoryForm.valid) {
      this.loaderView = true;

      this.accessoryForm.value.purchaseDate = this.accessoryForm.value.purchaseDate
        .split('-')
        .reverse()
        .join('/');

      const {
        typeId,
        quantity,
        name,
        ...otherFields
      } = this.accessoryForm.value;

      const properties = this.accessoryTypeData['configs'].map((config: any) => {
        if (config.units.length === 0) {
          return {
            name: config.name,
            value: otherFields[config.name] || null,
          };
        } else {
          return {
            name: config.name,
            unit: otherFields[config.name + 'Unit'] || null,
            value: otherFields[config.name] || null,
          };
        }
      });

      const payload = {
        typeId: this.accessoryTypeId,
        name,
        quantity,
        properties,
      };

      if (!this.accessoryId) {
        this.commonHttpService.addNewAccessory(payload).subscribe({
          next: (response: any) => {
            const accessoryId = response.data.id;
            this.toastrService.success('Accessory added successfully.', '', {
              toastClass: 'toaster-wrapper',
            });
            this.loaderView = false;
            this.router.navigate([`${ROUTES.ADMIN_INVENTORY}`]);
          },
          error: (err: any) => {
            this.loaderView = true;
            this.toastrService.error(
              err.error.error?.errorList[0]?.msg || err.error?.error?.errorList || 'Accessory not added',
              '',
              {
                toastClass: 'toaster-wrapper',
              }
            );
            this.loaderView = false;
            this.accessoryId
              ? this.router.navigate([`${ROUTES.ACCESSORY_DETAILS}${this.accessoryId}`])
              : this.router.navigate([`${ROUTES.ADD_ACCESSORY}`]);
          },
        });
      } else if (this.accessoryId) {
        this.commonHttpService.updateAccessory(this.accessoryId, payload).subscribe({
          next: (response: any) => {
            this.toastrService.success('Accessory updated successfully.', '', {
              toastClass: 'toaster-wrapper',
            });
            this.loaderView = false;
            this.router.navigate([`${ROUTES.ADMIN_INVENTORY}`]);
          },
          error: (err: any) => {
            this.loaderView = true;
            const errorMessage = err.error.error.errorList || 'Accessory not updated';
            this.toastrService.error(errorMessage, '', {
              toastClass: 'toaster-wrapper',
            });
            this.loaderView = false;
          },
        });
      }
    }
  }

  closeConfirmationDialog() {
    const dialogRef = this.dialog.open<string>(
      GenericConfirmationPopUpComponent,
      {
        panelClass: 'center-dialog-box',
        height: '11.75rem',
        backdropClass: 'backdrop',
        disableClose: true,
        data: {
          header: 'Are you sure you want to cancel?',
          description: 'Warning: This action will permanently discard all unsaved changes.',
          primaryBtn: 'Discard',
          secondaryBtn: 'Go Back',
          image: 'cautionImage',
        },
      }
    );
    dialogRef.closed.subscribe((response: any) => {
      if (response.buttonText === 'Discard') {
        this.router.navigate([ROUTES.ADMIN_INVENTORY]);
      }
    });
  }

  backToDetails() {
    const dialogRef = this.dialog.open<string>(
      GenericConfirmationPopUpComponent,
      {
        panelClass: 'center-dialog-box',
        height: '11.75rem',
        backdropClass: 'backdrop',
        disableClose: true,
        data: {
          header: 'Are you sure you want to cancel?',
          description: 'Warning: This action will permanently discard all unsaved changes.',
          primaryBtn: 'Discard',
          secondaryBtn: 'Go Back',
          image: 'cautionImage',
        },
      }
    );
    dialogRef.closed.subscribe((response: any) => {
      if (response.buttonText === 'Discard') {
        this.router.navigate([`${ROUTES.ADMIN_INVENTORY}`]);
      }
    });
  }

  showAddAccessoryConfirmationDialog() {
    const dialogRef = this.dialog.open<string>(
      GenericConfirmationPopUpComponent,
      {
        panelClass: 'center-dialog-box',
        height: '11.75rem',
        backdropClass: 'backdrop',
        disableClose: true,
        data: {
          header: 'Do you want to submit the form?',
          description: 'This action cannot be undone',
          primaryBtn: 'Submit Form',
          secondaryBtn: 'Go Back',
          image: 'cautionImage',
        },
      }
    );

    dialogRef.closed.subscribe((response: any) => {
      if (response.buttonText === 'Submit Form') {
        this.onSubmit();
      }
    });
  }

  getaccessoryDetails() {
    this.commonHttpService.getAccessoryDetails(this.accessoryId).subscribe({
      next: (res: any) => {
        this.accessoryDetails = res.data;
        this.accessoryTypeId = this.accessoryDetails.id;
        this.selectedValue = this.accessoryDetails.name;
        this.getValueOfCategory(this.accessoryTypeId);
      },
      error: (err) => {
        this.toastrService.error('Accessory Not Found', '', {
          toastClass: 'toaster-wrapper',
        });
      },
    });
  }

  fetchCategoryValueAndUpdateaccessory() {
    // After getValueOfCategory completes, execute the following functions
    this.updateAccessoryForm();
    this.updateAccessoryProperties();
    this.loaderView = false;
  }

  updateAccessoryForm() {
    const keys = [
      { key: 'typeId', value: this.accessoryTypeId },
      { key: 'name', value: this.accessoryDetails.name },
      { key: 'quantity', value: this.accessoryDetails.quantity },
      {
        key: 'purchaseDate',
        value: new Date(this.accessoryDetails?.purchaseDate)
          .toISOString()
          .split('T')[0],
      },
    ];

    keys.forEach((key) => {
      this.onSelectionChange(key.value, key.key);
    });
  }

  updateAccessoryProperties() {
    this.accessoryDetails.properties.map((property: any) => {
      this.onSelectionChange(property.value, property.name);
      if (property.unit) {
        this.onSelectionChange(property.unit, property.name + 'Unit');
      }
    });
  }
}
